import {
  Spin,
  Empty,
  Typography,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Card,
  Row,
  Col,
  Space,
  Checkbox,
} from 'antd';
import { EditOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAxiosPost from '../../hooks/useAxiosPost';
import useMultiNotification from '../../hooks/useNotification';
import { getUserType } from '../../helpers/userDetails';
import { formatDateOnly } from '../../helpers/date';
import './singlePackageComponent.css';

const { Option } = Select;

interface PackageDetails {
  intPkPackageID: number;
  intPkCompanyID: number;
  packageName: string;
  speedUp: number;
  speedDown: number;
  speedUnit: 'K' | 'M' | 'G';
  packageDurationEnum: 'HOURS' | 'DAYS';
  packageDurationDays: number;
  packageDurationHours: number;
  packageCreatedDate?: string;
  maxDataVolumeLimit?: number | null;
  maxDataVolumeUnit?: 'K' | 'M' | 'G' | null;
}

const SinglePackageComponent = () => {
  const { intPkPackageID } = useParams();
  const navigate = useNavigate();
  const { openNotification, contextHolder } = useMultiNotification();
  const [form] = Form.useForm();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [packageDetails, setPackageDetails] = useState<PackageDetails | null>(null);
  const [packageDurationEnum, setPackageDurationEnum] = useState<'HOURS' | 'DAYS' | undefined>(undefined);
  const [maxDataVolumeProvided, setMaxDataVolumeProvided] = useState(false);
  const postApiDataRef = useRef<any>(null);
  const hasProcessedUpdateSuccess = useRef(false);

  const [postApiData, apiResponse, apiError, apiLoading, apiMessage, apiErrorCount] =
    useAxiosPost<ResponseProp>('packages/details');

  const [
    updatePackageData,
    updatePackageResponse,
    updatePackageError,
    updatePackageLoading,
    updatePackageMessage,
    updatePackageErrorCount,
  ] = useAxiosPost<ResponseProp>('packages/update');

  useEffect(() => {
    postApiDataRef.current = postApiData;
  }, [postApiData]);

  useEffect(() => {
    if (intPkPackageID) {
      postApiDataRef.current({ id: Number(intPkPackageID) });
    }
  }, [intPkPackageID]);

  useEffect(() => {
    if (apiResponse !== null) {
      const responseData: any = apiResponse.responseData;
      if (responseData) {
        setPackageDetails(responseData);
        // Set form values for edit modal
        form.setFieldsValue({
          packageName: responseData.packageName,
          speedUp: responseData.speedUp,
          speedDown: responseData.speedDown,
          speedUnit: responseData.speedUnit,
          packageDurationEnum: responseData.packageDurationEnum,
          packageDurationDays: responseData.packageDurationDays,
          packageDurationHours: responseData.packageDurationHours,
          maxDataVolumeLimit: responseData.maxDataVolumeLimit,
          maxDataVolumeUnit: responseData.maxDataVolumeUnit,
          hasDataLimit: !!(responseData.maxDataVolumeLimit != null && responseData.maxDataVolumeUnit != null),
        });
        setPackageDurationEnum(responseData.packageDurationEnum);
        setMaxDataVolumeProvided(
          responseData.maxDataVolumeLimit != null && responseData.maxDataVolumeUnit != null,
        );
      }
    }
  }, [apiResponse, form]);

  useEffect(() => {
    if (apiError !== null && apiErrorCount !== 0) {
      openNotification('error', 'Failed to Load Package', apiError);
    }
  }, [apiError, apiErrorCount, openNotification]);

  // Handle update package success
  useEffect(() => {
    if (updatePackageResponse !== null && !hasProcessedUpdateSuccess.current) {
      hasProcessedUpdateSuccess.current = true;
      openNotification('success', 'Package Updated', 'Package updated successfully');
      setIsEditModalOpen(false);
      // Refresh package details
      setTimeout(() => {
        if (intPkPackageID) {
          postApiDataRef.current({ id: Number(intPkPackageID) });
        }
      }, 100);
    }
  }, [updatePackageResponse, intPkPackageID]);

  // Reset the flag when modal opens or updatePackageResponse becomes null
  useEffect(() => {
    if (updatePackageResponse === null) {
      hasProcessedUpdateSuccess.current = false;
    }
  }, [updatePackageResponse]);

  // Handle update package error
  useEffect(() => {
    if (updatePackageError !== null && updatePackageErrorCount !== 0) {
      openNotification('error', 'Failed to Update Package', updatePackageError);
    }
  }, [updatePackageError, updatePackageErrorCount, openNotification]);

  const showEditModal = () => {
    if (packageDetails) {
      form.setFieldsValue({
        packageName: packageDetails.packageName,
        speedUp: packageDetails.speedUp,
        speedDown: packageDetails.speedDown,
        speedUnit: packageDetails.speedUnit,
        packageDurationEnum: packageDetails.packageDurationEnum,
        packageDurationDays: packageDetails.packageDurationDays,
        packageDurationHours: packageDetails.packageDurationHours,
        maxDataVolumeLimit: packageDetails.maxDataVolumeLimit,
        maxDataVolumeUnit: packageDetails.maxDataVolumeUnit,
        hasDataLimit: !!(packageDetails.maxDataVolumeLimit != null && packageDetails.maxDataVolumeUnit != null),
      });
      setPackageDurationEnum(packageDetails.packageDurationEnum);
      setMaxDataVolumeProvided(
        packageDetails.maxDataVolumeLimit != null && packageDetails.maxDataVolumeUnit != null,
      );
      setIsEditModalOpen(true);
    }
  };

  const handleCancel = () => {
    if (!updatePackageLoading) {
      setIsEditModalOpen(false);
    }
  };

  const handleDataLimitCheckboxChange = (e: { target: { checked: boolean } }) => {
    const checked = e.target.checked;
    setMaxDataVolumeProvided(checked);
    if (!checked) {
      form.setFieldsValue({
        maxDataVolumeLimit: undefined,
        maxDataVolumeUnit: undefined,
      });
    }
  };

  const handleUpdate = (values: any) => {
    const {
      packageName,
      speedUp,
      speedDown,
      speedUnit,
      packageDurationEnum: durationEnum,
      packageDurationDays,
      packageDurationHours,
      maxDataVolumeLimit,
      maxDataVolumeUnit,
    } = values;

    if (intPkPackageID) {
      updatePackageData({
        id: Number(intPkPackageID),
        packageName: packageName.trim(),
        speedUp,
        speedDown,
        speedUnit,
        packageDurationEnum: durationEnum,
        packageDurationDays: durationEnum === 'DAYS' ? packageDurationDays : 0,
        packageDurationHours: durationEnum === 'HOURS' ? packageDurationHours : 0,
        maxDataVolumeLimit: maxDataVolumeProvided && maxDataVolumeLimit != null ? maxDataVolumeLimit : null,
        maxDataVolumeUnit: maxDataVolumeProvided && maxDataVolumeUnit ? maxDataVolumeUnit : null,
      });
    }
  };

  const handleModalClose = () => {
    if (!updatePackageLoading) {
      handleCancel();
    }
  };

  const getSpeedDisplay = (speed: number, unit: string) => {
    return `${speed} ${unit}bps`;
  };

  const getDurationDisplay = () => {
    if (!packageDetails) return '-';
    if (packageDetails.packageDurationEnum === 'HOURS') {
      return `${packageDetails.packageDurationHours} Hour${packageDetails.packageDurationHours !== 1 ? 's' : ''}`;
    } else {
      return `${packageDetails.packageDurationDays} Day${packageDetails.packageDurationDays !== 1 ? 's' : ''}`;
    }
  };

  const getDataVolumeDisplay = () => {
    if (!packageDetails) return '-';
    if (
      packageDetails.maxDataVolumeLimit != null &&
      packageDetails.maxDataVolumeUnit != null
    ) {
      return `${packageDetails.maxDataVolumeLimit} ${packageDetails.maxDataVolumeUnit}B`;
    }
    return 'No limit';
  };

  const getSpeedUnitLabel = (unit: string) => {
    const labels: { [key: string]: string } = {
      K: 'Kilobits per second (Kbps)',
      M: 'Megabits per second (Mbps)',
      G: 'Gigabits per second (Gbps)',
    };
    return labels[unit] || unit;
  };

  if (apiLoading) {
    return (
      <>
        {contextHolder}
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <Typography style={{ marginTop: '16px', display: 'block' }}>Loading package details...</Typography>
        </div>
      </>
    );
  }

  if (apiError && !apiLoading) {
    return (
      <>
        {contextHolder}
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Empty
            description={
              <Typography.Text type="danger">{apiError || 'Failed to load package details'}</Typography.Text>
            }
          />
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/packages')}
            style={{ marginTop: '16px' }}
          >
            Back to Packages
          </Button>
        </div>
      </>
    );
  }

  if (!packageDetails) {
    return (
      <>
        {contextHolder}
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Empty description="Package not found" />
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/packages')}
            style={{ marginTop: '16px' }}
          >
            Back to Packages
          </Button>
        </div>
      </>
    );
  }

  const userRole = getUserType();
  const canEditPackage = userRole === 'ADMIN';

  return (
    <>
      {contextHolder}
      <div style={{ marginBottom: '16px' }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/packages')}>
            Back
          </Button>
          {canEditPackage && (
            <Button type="primary" icon={<EditOutlined />} onClick={showEditModal}>
              Edit Package
            </Button>
          )}
        </Space>
      </div>

      <Card title="Package Details" style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Typography.Text strong>Package Name:</Typography.Text>
            <Typography.Paragraph style={{ marginTop: '4px' }}>{packageDetails.packageName}</Typography.Paragraph>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Typography.Text strong>Download Speed:</Typography.Text>
            <Typography.Paragraph style={{ marginTop: '4px' }}>
              {getSpeedDisplay(packageDetails.speedDown, packageDetails.speedUnit)}
            </Typography.Paragraph>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Typography.Text strong>Upload Speed:</Typography.Text>
            <Typography.Paragraph style={{ marginTop: '4px' }}>
              {getSpeedDisplay(packageDetails.speedUp, packageDetails.speedUnit)}
            </Typography.Paragraph>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Typography.Text strong>Speed Unit:</Typography.Text>
            <Typography.Paragraph style={{ marginTop: '4px' }}>
              {getSpeedUnitLabel(packageDetails.speedUnit)}
            </Typography.Paragraph>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Typography.Text strong>Duration Type:</Typography.Text>
            <Typography.Paragraph style={{ marginTop: '4px' }}>
              {packageDetails.packageDurationEnum}
            </Typography.Paragraph>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Typography.Text strong>Duration:</Typography.Text>
            <Typography.Paragraph style={{ marginTop: '4px' }}>{getDurationDisplay()}</Typography.Paragraph>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Typography.Text strong>Data Limit:</Typography.Text>
            <Typography.Paragraph style={{ marginTop: '4px' }}>{getDataVolumeDisplay()}</Typography.Paragraph>
          </Col>
          {packageDetails.packageCreatedDate && (
            <Col xs={24} sm={12} md={8}>
              <Typography.Text strong>Date Created:</Typography.Text>
              <Typography.Paragraph style={{ marginTop: '4px' }}>
                {formatDateOnly(packageDetails.packageCreatedDate)}
              </Typography.Paragraph>
            </Col>
          )}
        </Row>
      </Card>

      <Modal
        title="Edit Package"
        open={isEditModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        maskClosable={!updatePackageLoading}
        closable={!updatePackageLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleUpdate} requiredMark={false}>
          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Package Name</span>}
            name="packageName"
            rules={[
              {
                required: true,
                message: 'Please enter package name',
              },
              {
                min: 1,
                message: 'Package name cannot be empty',
              },
              {
                max: 45,
                message: 'Package name must be less than 45 characters',
              },
              {
                whitespace: true,
                message: 'Package name cannot be only spaces',
              },
            ]}
          >
            <Input placeholder="Enter package name" disabled={updatePackageLoading} maxLength={45} showCount />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Download Speed</span>}
            name="speedDown"
            rules={[
              {
                required: true,
                message: 'Please enter download speed',
              },
              {
                type: 'number',
                min: 1,
                message: 'Download speed must be greater than 0',
              },
            ]}
          >
            <InputNumber
              placeholder="Enter download speed"
              disabled={updatePackageLoading}
              style={{ width: '100%' }}
              min={1}
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Upload Speed</span>}
            name="speedUp"
            rules={[
              {
                required: true,
                message: 'Please enter upload speed',
              },
              {
                type: 'number',
                min: 1,
                message: 'Upload speed must be greater than 0',
              },
            ]}
          >
            <InputNumber
              placeholder="Enter upload speed"
              disabled={updatePackageLoading}
              style={{ width: '100%' }}
              min={1}
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Speed Unit</span>}
            name="speedUnit"
            rules={[
              {
                required: true,
                message: 'Please select speed unit',
              },
            ]}
          >
            <Select placeholder="Select speed unit" disabled={updatePackageLoading}>
              <Option value="K">K (Kilobits)</Option>
              <Option value="M">M (Megabits)</Option>
              <Option value="G">G (Gigabits)</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Duration Type</span>}
            name="packageDurationEnum"
            rules={[
              {
                required: true,
                message: 'Please select duration type',
              },
            ]}
          >
            <Select
              placeholder="Select duration type"
              disabled={updatePackageLoading}
              onChange={(value) => {
                setPackageDurationEnum(value);
                form.setFieldsValue({
                  packageDurationDays: undefined,
                  packageDurationHours: undefined,
                });
              }}
            >
              <Option value="HOURS">Hours</Option>
              <Option value="DAYS">Days</Option>
            </Select>
          </Form.Item>

          {packageDurationEnum === 'HOURS' && (
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Number of Hours</span>}
              name="packageDurationHours"
              rules={[
                {
                  required: true,
                  message: 'Please enter number of hours',
                },
                {
                  type: 'number',
                  min: 1,
                  message: 'Hours must be greater than 0',
                },
              ]}
            >
              <InputNumber
                placeholder="Enter number of hours"
                disabled={updatePackageLoading}
                style={{ width: '100%' }}
                min={1}
              />
            </Form.Item>
          )}

          {packageDurationEnum === 'DAYS' && (
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Number of Days</span>}
              name="packageDurationDays"
              rules={[
                {
                  required: true,
                  message: 'Please enter number of days',
                },
                {
                  type: 'number',
                  min: 1,
                  message: 'Days must be greater than 0',
                },
              ]}
            >
              <InputNumber
                placeholder="Enter number of days"
                disabled={updatePackageLoading}
                style={{ width: '100%' }}
                min={1}
              />
            </Form.Item>
          )}

          <Form.Item name="hasDataLimit" valuePropName="checked">
            <Checkbox onChange={handleDataLimitCheckboxChange} disabled={updatePackageLoading}>
              Data Limit
            </Checkbox>
          </Form.Item>

          {maxDataVolumeProvided && (
            <>
              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Max Data Volume Limit</span>}
                name="maxDataVolumeLimit"
                rules={[
                  {
                    required: true,
                    message: 'Please enter max data volume limit',
                  },
                  {
                    type: 'number',
                    min: 1,
                    message: 'Max data volume limit must be greater than 0',
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter max data volume limit"
                  disabled={updatePackageLoading}
                  style={{ width: '100%' }}
                  min={1}
                />
              </Form.Item>

              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Max Data Volume Unit</span>}
                name="maxDataVolumeUnit"
                rules={[
                  {
                    required: true,
                    message: 'Please select max data volume unit',
                  },
                ]}
              >
                <Select placeholder="Select max data volume unit" disabled={updatePackageLoading}>
                  <Option value="K">K (Kilobytes)</Option>
                  <Option value="M">M (Megabytes)</Option>
                  <Option value="G">G (Gigabytes)</Option>
                </Select>
              </Form.Item>
            </>
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={handleCancel} style={{ marginRight: '8px' }} disabled={updatePackageLoading}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={updatePackageLoading} disabled={updatePackageLoading}>
              {updatePackageLoading ? 'Updating...' : 'Update Package'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
export default SinglePackageComponent;
